// Shared helpers for the post-office COD complaint workflow.
//
// When a COD/VPP parcel's payment is still pending 30+ days after the
// order date, staff file a complaint with the post office and record the
// returned complaint id against the order. Both surfaces that expose this
// — the COD Pending Payments page and the Inbox payment-overdue reminder —
// build the same draft message to copy into the post-office portal, so the
// wording lives here in one place.

// Accepts either an ISO datetime ("2026-03-25T..."), a "dd-mm-yyyy" string
// (as the pending-payments API returns), or a Date. Returns "dd/mm/yyyy".
export function formatComplaintDate (value) {
  if (!value) return ''
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return ddmmyyyy(value.getDate(), value.getMonth() + 1, value.getFullYear())
  }
  const raw = String(value)
  // dd-mm-yyyy → as-is (just swap separators)
  const dmY = raw.match(/^(\d{2})-(\d{2})-(\d{4})$/)
  if (dmY) return `${dmY[1]}/${dmY[2]}/${dmY[3]}`
  // ISO yyyy-mm-dd (optionally with time)
  const ymd = raw.slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (ymd) return `${ymd[3]}/${ymd[2]}/${ymd[1]}`
  const d = new Date(raw)
  if (!Number.isNaN(d.getTime())) {
    return ddmmyyyy(d.getDate(), d.getMonth() + 1, d.getFullYear())
  }
  return raw
}

function ddmmyyyy (d, m, y) {
  return `${String(d).padStart(2, '0')}/${String(m).padStart(2, '0')}/${y}`
}

// Build the complaint message to paste into the post-office complaint
// portal. `order` may come from the pending-payments row shape
// (order_date as "dd-mm-yyyy", post_office_name, grand_total) or from a
// full order record (order_date ISO, contact_postofficename). We read
// whichever fields are present.
export function buildComplaintDraft (order) {
  if (!order) return ''
  const bookedOn = formatComplaintDate(order.order_date || order.created_at)
  const postOffice = (order.post_office_name || order.contact_postofficename || '').trim()
  const orderNo = order.order_number || ''
  const tracking = order.tracking_id || ''
  const amount = order.grand_total != null ? Number(order.grand_total) : null

  const lines = []
  const officeClause = postOffice ? ` to the ${postOffice}` : ''
  const dateClause = bookedOn ? ` on ${bookedOn}` : ''
  lines.push(`We booked a COD parcel${dateClause}${officeClause} but have not received the COD amount until today. Please do the needful.`)
  lines.push('')
  if (orderNo) lines.push(`Order No: ${orderNo}`)
  if (tracking) lines.push(`Tracking / Article No: ${tracking}`)
  if (amount != null && !Number.isNaN(amount)) lines.push(`COD Amount: ₹${amount.toFixed(2)}`)
  return lines.join('\n')
}

// Copy text to the clipboard, with a legacy fallback for non-secure
// contexts. Resolves true on success.
export async function copyToClipboard (text) {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text)
      return true
    }
  } catch (e) { /* fall through to legacy path */ }
  try {
    const ta = document.createElement('textarea')
    ta.value = text
    ta.style.position = 'fixed'
    ta.style.opacity = '0'
    document.body.appendChild(ta)
    ta.focus()
    ta.select()
    const ok = document.execCommand('copy')
    document.body.removeChild(ta)
    return ok
  } catch (e) {
    return false
  }
}
